import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  IconButton
} from '@mui/material';
import { useForm } from 'react-hook-form';
import FormInput from '../../../../components/FormFields/FormFields';
import PageButton from '../../../../components/PageButton';
import { Rule, Visibility, VisibilityOff } from '@mui/icons-material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateAdminStaffMutation, useUpdateAdminStaffMutation } from 'src/reduxServer/services/adminStaff';
import { showToast } from 'src/utils/toastfy';
import { use } from 'i18next';
import { useTheme } from "@mui/material";


export interface StaffFormValues {
  first_name: string;
  last_name: string;
  id?: number;
  email: string;
  mobile: string;
  dob: string;
  gender: string;
  ID_type: string;
  ID_num: string;
  status: string;
  password: string
}

interface Props {
  open: boolean;
  onCancel: () => void;
  updateData?: StaffFormValues | null;
}

const defaultValues: StaffFormValues = {
  first_name: '',
  last_name: '',
  email: '',
  mobile: '',
  dob: '',
  gender: '',
  ID_type: '',
  ID_num: '',
  status: 'Active',
  password: ""
};

const AddStaff = ({ open, onCancel, updateData }: Props) => {
  const { control, handleSubmit, reset } = useForm<StaffFormValues>({
    defaultValues
  });
  const theme = useTheme();

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false);

  const [addStafaApi, { error }] = useCreateAdminStaffMutation()
  const [updateStaff] = useUpdateAdminStaffMutation()

  useEffect(() => {
    if (!open) return;
    if (updateData) reset(updateData);
    else reset(defaultValues);
  }, [open, updateData, reset]);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };


  const onSubmit = async (data: StaffFormValues) => {
    console.log("Add Staff Data>>>", data)
    setLoading(true)
    try {

      if (!updateData?.id) {
        const result = await addStafaApi(data)
        console.log("staff add result>>>", result)
        if (result?.data?.id) {
          showToast("success", t("Admin staff created successfully"))
          setLoading(false)
        } else {
          let apiErrors;
          if (result?.error && typeof result.error === 'object' && 'data' in result.error) {
            // @ts-ignore
            apiErrors = result.error.data;
          }

          if (apiErrors) {
            const message = Object.values(apiErrors)
              .flat()
              .join(", ");

            showToast("error", message);
          }
          setLoading(false)
        }

      } else {
        const result = await updateStaff({ id: updateData.id, data: data })
        if (result.data?.success === true) {
          showToast("success", t(result?.data?.message) || t("Admin staff updated successfully"))
        } else {
          showToast("error", result?.data?.errors || t("Update failed"))
        }
        setLoading(false)
      }
    } catch (error: any) {
      console.log("Add Staff Errors>>", error);



      showToast("error", t("An error occurred"));


      setLoading(false);
    }
    reset(defaultValues);
    onCancel();
    setLoading(false);

  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{updateData ? t('Edit_Staff') : t('Add Staff')}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={6}>
              <FormInput
                name="first_name"
                label={t("First Name")}
                control={control}
                rules={{ required: t('This Field is Required') }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormInput
                name="last_name"
                label={t("Last Name")}
                control={control}
                rules={{ required: t('This Field is Required') }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                name="email"
                label={t("Email")}
                control={control}
                rules={{ required: t('This Field is Required') }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                name="mobile"
                label={t("Mobile")}
                control={control}
                rules={{ required: t('This Field is Required') }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                name="dob"
                label={t("dob")}
                type="date"
                control={control}
                InputLabelProps={{ shrink: true }}
                rules={{ required: t('This Field is Required') }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                name="ID_type"
                label={t("ID Type")}
                select
                control={control}
                options={[
                  { value: 'I', label: t('Iqama') },
                  { value: 'N', label: t('National ID') }
                ]}
                rules={{ required: t('This Field is Required') }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInput
                name="ID_num"
                label={t("ID Number")}
                control={control}
                rules={{ required: t('This Field is Required') }}
              />
            </Grid>
            {
              !updateData?.id && (
                <Grid item xs={12}>
                  <FormInput
                    name={"password"}
                    label={t("passWord")}
                    type={showPassword ? "text" : "password"}
                    rules={{ required: t('This Field is Required') }}
                    control={control}
                    sx={{
                      mb: 2,
                      "& .MuiInputBase-root": {
                        backgroundColor: theme.palette.background.default
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleTogglePassword} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              )
            }

            <Grid item xs={12}>
              <FormControl>
                <FormLabel>{t("Gender")}</FormLabel>

                <Controller
                  name="gender"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup row {...field}>
                      <FormControlLabel
                        value="M"
                        control={<Radio />}
                        label={t("Male")}
                      />
                      <FormControlLabel
                        value="F"
                        control={<Radio />}
                        label={t("Female")}
                      />
                    </RadioGroup>
                  )}
                  rules={{ required: t('This Field is Required') }}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <DialogActions sx={{ px: 0 }}>
                <PageButton variant="outlined" onClick={onCancel} sx={{
                  ml: "10px"
                }}>
                  {t("Cancel")}
                </PageButton>
                <PageButton
                  loading={loading}
                  type="submit" variant="contained">
                  {updateData?.id ? t("Update") : t("Save")}
                </PageButton>
              </DialogActions>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddStaff;
